import { formatMoney, rateForCadence } from '@app/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { readCivilDate } from '../lib/today'
import type { RouterOutputs } from '../lib/trpc'
import { useTRPC } from '../lib/trpc'
import {
  Button,
  Choice,
  DateInput,
  Disclosure,
  Field,
  MoneyInput,
  Notice,
  PaymentStep,
  Select,
  useCheckoutConfirmation,
} from '../ui'
import { useConfirm } from '../ui/useConfirm'

type View = RouterOutputs['pay']['membership']

/**
 * A household's membership, reached from a link: the member's account link,
 * or the card link the office gave them for automatic payments.
 *
 * It opens on what is on record and two acts — the card, and Edit, which
 * shows every step already answered from the record. A card on file with no
 * automatic payments behind it is one press from turning them on, with the
 * words agreed to one press further.
 *
 * Leaving is the member's own act, and it is one of two: stop the card, and
 * the membership stands with its periods billed with no card; or end the
 * membership, and nothing more is raised. Each confirms first, saying what it
 * does, and happens at once.
 */
export function PayMembership({
  token,
  view,
  startEditing = false,
  besidePayment = false,
  onEnded,
  onCardChange,
  onConfirmingChange,
}: {
  token: string
  view: View
  /** Opened from an Edit elsewhere, so the steps are what is shown first. */
  startEditing?: boolean
  /**
   * Drawn on the account page under the way to pay, whose commit is the
   * page's one confident mark, so the card here is a secondary act.
   */
  besidePayment?: boolean
  /** Told once the membership has ended, so the page can say so. */
  onEnded?: () => void
  /** Told as the card is typed, so a kiosk counts it as activity. */
  onCardChange?: () => void
  /** Told while a card is being confirmed, so a kiosk holds its idle. */
  onConfirmingChange?: (confirming: boolean) => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const current = view.arrangement
  const firstTier = current
    ? (current.tierItemId ?? '')
    : (view.tiers[0]?.id ?? '')
  const startCadence = current?.cadence ?? 'monthly'
  const firstTierRow = view.tiers.find((item) => item.id === firstTier)
  const startAmount = (
    (current?.rateCents ??
      (firstTierRow
        ? rateForCadence(firstTierRow.monthlyCents, startCadence)
        : 0)) / 100
  ).toFixed(2)
  /* Starting a membership has nothing on record to show, so it is all steps. */
  const [editing, setEditing] = useState(startEditing || !current)
  const [tierId, setTierId] = useState(firstTier)
  const [cadence, setCadence] = useState<'monthly' | 'annual'>(startCadence)
  const tier = view.tiers.find((item) => item.id === tierId)
  const [amount, setAmount] = useState(startAmount)
  const [startsOn, setStartsOn] = useState(view.today)
  /* Who the agreement is recorded as is the link's to say, never asked: a
     card may be anybody's, and the person agreeing is not whose card it is. */
  const personId = view.inNameOf ?? ''
  const rateCents = Math.round(Number(amount) * 100)
  /* A change may keep the standing tier, or stand on none; a start names one. */
  const valid =
    (current !== null || tierId !== '') && rateCents > 0 && personId !== ''
  const consent = useQuery({
    ...trpc.pay.membershipConsent.queryOptions({
      token,
      ...(tierId ? { tierId } : {}),
      rateCents: valid ? rateCents : 1,
      cadence,
    }),
    enabled: valid,
    retry: false,
  })
  const key = trpc.pay.membership.queryKey({ token })
  /* What the record says and what agreeing to it means are read together: a
     card arriving turns "nothing to agree to" into terms that must be shown,
     so the answer about consent is never left from before the card. */
  const refresh = useCallback(
    () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: key }),
        qc.invalidateQueries({
          queryKey: trpc.pay.membershipConsent.queryKey(),
        }),
      ]),
    [qc, key, trpc],
  )
  const hasCard = view.hasCard
  // Whether a card is on file is what the consent answer turns on.
  useEffect(() => {
    if (hasCard === undefined) return
    void qc.invalidateQueries({
      queryKey: trpc.pay.membershipConsent.queryKey(),
    })
  }, [hasCard, qc, trpc])
  const change = useMutation({
    ...trpc.pay.membershipChange.mutationOptions(),
    onSuccess: () => {
      setEditing(false)
      return refresh()
    },
    onError: () => refresh(),
  })
  const start = useMutation({
    ...trpc.pay.membershipStart.mutationOptions(),
    onSuccess: () => {
      setEditing(false)
      return refresh()
    },
    onError: () => refresh(),
  })
  /* A saved card is recorded by Stripe's webhook, a moment after the card
     form says it is done; until then the page says so and looks again, a
     bounded number of times, rather than offering the card a second time. */
  const [cardSaving, setCardSaving] = useState(false)
  const [cardSlow, setCardSlow] = useState(false)
  useEffect(() => {
    if (!cardSaving) return
    if (hasCard) {
      setCardSaving(false)
      return
    }
    let tries = 0
    const timer = setInterval(() => {
      tries += 1
      if (tries > 15) {
        clearInterval(timer)
        setCardSaving(false)
        setCardSlow(true)
        return
      }
      void refresh()
    }, 2_000)
    return () => clearInterval(timer)
  }, [cardSaving, hasCard, refresh])
  const card = useMutation({ ...trpc.pay.membershipCard.mutationOptions() })
  /* Either act changes what the rest of the account page states — what is
     paying automatically, and the card named for it — so those are read
     again with the membership. */
  const afterAct = () =>
    Promise.all([
      refresh(),
      qc.invalidateQueries({ queryKey: trpc.pay.autopay.queryKey() }),
      qc.invalidateQueries({ queryKey: trpc.pay.account.queryKey() }),
      qc.invalidateQueries({ queryKey: trpc.pay.bill.queryKey() }),
    ])
  const stopCard = useMutation({
    ...trpc.pay.membershipStopCard.mutationOptions(),
    onSuccess: () => afterAct(),
  })
  const end = useMutation({
    ...trpc.pay.membershipEnd.mutationOptions(),
    onSuccess: () => {
      onEnded?.()
      return afterAct()
    },
  })
  const { confirm, dialog } = useConfirm()
  const named = current?.tierLabel ?? 'your membership'
  const confirmStopCard = async () => {
    if (!current) return
    const accepted = await confirm({
      title: 'Stop the card?',
      body: `Your card will no longer pay ${named}. The membership stands, and its later periods are billed to your account with no card, to pay the way you choose. ${view.orgName} will be told.`,
      verb: 'Stop the card',
      // The membership and everything on the account stay as they are.
      destructive: false,
    })
    if (accepted) stopCard.mutate({ token, arrangementId: current.id })
  }
  const confirmEnd = async () => {
    if (!current) return
    const accepted = await confirm({
      title: 'End your membership?',
      body: `${named} ends today: no later period is billed${current.automaticPayments ? ', and the card stops paying it' : ''}. Anything already owed on your account stays owed. ${view.orgName} will be told.`,
      verb: 'End membership',
      destructive: true,
    })
    if (accepted) end.mutate({ token, arrangementId: current.id })
  }
  const confirmation = useCheckoutConfirmation({
    onComplete: () => {
      card.reset()
      setCardSlow(false)
      setCardSaving(true)
      void refresh()
    },
    writeCheckoutToUrl: true,
  })

  if (card.data) {
    return (
      <PaymentStep
        orgId={view.orgShortId}
        card={card.data.card}
        what="Automatic payments"
        who={view.accountName}
        amount="No charge"
        payLabel="Save card"
        confirmation={confirmation}
        onCardChange={onCardChange}
        onConfirmingChange={onConfirmingChange}
        onBack={() => card.reset()}
      />
    )
  }

  const error = change.error ?? start.error ?? card.error
  const save = () => {
    if (!valid || !consent.data) return
    if (current) {
      change.mutate({
        token,
        arrangementId: current.id,
        ...(tierId ? { tierId } : {}),
        rateCents,
        cadence,
        personId,
        consentText: consent.data.consentText,
      })
    } else {
      start.mutate({
        token,
        tierId,
        rateCents,
        cadence,
        startsOn,
        personId,
        consentText: consent.data.consentText,
      })
    }
  }
  /* Opening or leaving the steps starts them clean: no word left over from
     the last save or the last failure. Leaving puts every answer back. */
  const openSteps = () => {
    change.reset()
    start.reset()
    setEditing(true)
  }
  const stopEditing = () => {
    setTierId(firstTier)
    setCadence(startCadence)
    setAmount(startAmount)
    change.reset()
    start.reset()
    setEditing(false)
  }
  /* What this visit's save would ask the member to agree to. */
  const agrees = consent.data?.needed === true
  const turnOn =
    !editing && current !== null && !current.automaticPayments && agrees
  const busy = change.isPending || start.isPending
  const cardButton = view.acceptsPayments ? (
    <Button
      variant={editing || turnOn || besidePayment ? 'secondary' : 'default'}
      disabled={!personId || card.isPending}
      onClick={() => card.mutate({ token, personId })}
    >
      {view.hasCard ? 'Replace card' : 'Add card'}
    </Button>
  ) : null

  return (
    <section className="pay__autopay">
      <h2 className="pay__h2">Your membership</h2>
      {current ? (
        <div className="pay__auto">
          <p className="pay__auto-head">
            {current.tierLabel ?? 'Membership'} ·{' '}
            {formatMoney(current.rateCents)}{' '}
            {current.cadence === 'annual' ? 'a year' : 'a month'}
          </p>
          <p className="pay__auto-text">
            Started {readCivilDate(current.startsOn)}
            {current.lastCoveredDay
              ? ` · Paid through ${readCivilDate(current.lastCoveredDay)}`
              : ''}
            {` · Automatic payments ${current.automaticPayments ? 'on' : 'off'}`}
            {view.hasCard ? ' · A card is on file' : ' · No card on file'}
          </p>
        </div>
      ) : (
        <Notice>No membership is currently standing on this account.</Notice>
      )}

      {editing && view.tiers.length > 0 && (
        <Field label="Membership">
          <Select
            value={tierId}
            onValueChange={(value) => {
              setTierId(value)
              const next = view.tiers.find((item) => item.id === value)
              if (next)
                setAmount(
                  (rateForCadence(next.monthlyCents, cadence) / 100).toFixed(2),
                )
            }}
            options={[
              ...(current?.tierItemId === null
                ? [{ value: '', label: 'No tier' }]
                : []),
              ...view.tiers.map((item) => ({
                value: item.id,
                label: item.label,
              })),
            ]}
          />
        </Field>
      )}
      {editing && (
        <Field label="Amount">
          <MoneyInput
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>
      )}
      {editing && (!current || current.cadenceCanChange) && (
        <Choice
          label="How often?"
          value={cadence}
          onValueChange={(value) => {
            setCadence(value)
            if (tier) {
              setAmount(
                (rateForCadence(tier.monthlyCents, value) / 100).toFixed(2),
              )
            }
          }}
          options={[
            { value: 'monthly', label: 'Monthly' },
            { value: 'annual', label: 'Yearly' },
          ]}
        />
      )}
      {editing && !current && (
        <Field label="Starts on">
          <DateInput
            value={startsOn}
            min={view.today}
            onChange={(event) => setStartsOn(event.target.value)}
          />
        </Field>
      )}
      {(editing || turnOn) && agrees && consent.data && (
        <Disclosure title="Terms and conditions" flush>
          <p className="pay__auto-text">{consent.data.consentText}</p>
        </Disclosure>
      )}
      {error && <Notice tone="error">{error.message}</Notice>}
      {(stopCard.error ?? end.error) && (
        <Notice tone="error">{(stopCard.error ?? end.error)?.message}</Notice>
      )}
      {(change.isSuccess || start.isSuccess) && !error && (
        <Notice>Your membership is saved.</Notice>
      )}
      {stopCard.isSuccess && (
        <Notice>
          The card no longer pays your membership. Later periods are billed to
          your account.
        </Notice>
      )}
      {end.isSuccess && (
        <Notice>
          Your membership has ended. Anything already owed stays on your
          account.
        </Notice>
      )}
      {cardSaving && !hasCard && (
        <Notice>Saving your card — this takes a moment.</Notice>
      )}
      {cardSlow && !hasCard && (
        <Notice>
          Your card is taking longer than usual to record. Reload this page in a
          minute; there is no need to add it again.
        </Notice>
      )}
      {!personId && (
        <Notice>
          This account has nobody on file to agree for it. Please contact the
          office.
        </Notice>
      )}

      {cardSaving && !hasCard ? null : editing && !current && !hasCard ? (
        /* A new membership starts on a card, so with none on file the card
           is the next step, and Start follows once it is saved. */
        view.acceptsPayments ? (
          <Button
            disabled={!valid || card.isPending}
            onClick={() => card.mutate({ token, personId })}
          >
            Next: add a card
          </Button>
        ) : (
          <Notice>
            {view.orgName} is not taking cards online yet. Please contact the
            office to start a membership.
          </Notice>
        )
      ) : editing ? (
        <>
          <Button disabled={!valid || !consent.data || busy} onClick={save}>
            {current ? 'Save membership' : 'Start membership'}
          </Button>
          {cardButton}
          {current && (
            <Button variant="quiet" onClick={stopEditing}>
              Back
            </Button>
          )}
        </>
      ) : (
        <>
          {turnOn && (
            <Button
              variant={besidePayment ? 'secondary' : 'default'}
              disabled={!valid || busy}
              onClick={save}
            >
              Turn on automatic payments
            </Button>
          )}
          {cardButton}
          <Button variant="secondary" onClick={openSteps}>
            Edit
          </Button>
          {current?.automaticPayments && (
            <Button
              variant="quiet"
              disabled={stopCard.isPending || end.isPending}
              onClick={() => void confirmStopCard()}
            >
              Stop the card
            </Button>
          )}
          {current && (
            <Button
              variant="quiet"
              disabled={stopCard.isPending || end.isPending}
              onClick={() => void confirmEnd()}
            >
              End my membership
            </Button>
          )}
        </>
      )}
      {dialog}
    </section>
  )
}
